/* eslint-disable */


import {CommonData} from "../util/common/common-data";
import {Form} from "../form/form.service";
import {CustomStatisticsResource} from "./custom-statistics-resource";
import {Set} from "immutable";
import {DqlQuery, Query} from "../query/field";
import {FilterField} from "../query/filterfields";
import {OrderField} from "../query/orderfields";
import {PagingRequest} from "../util/services";

/* eslint-enable */

export namespace CustomStatistics {

  export interface QueryRequest {
    fields?: Query.FieldFunction<Fields.CustomStatistics>;
    filter?: Query.FilterFunction<FilterField.CustomStatistics>;
    order?: Query.OrderFunction<OrderField.CustomStatistics>;
    paging?: PagingRequest;
    rights?: Set<string>;
    noProgressBar?: boolean;
  }

  export interface CustomStatistics {
    id: number;
    grantedRights?: Set<string>;
    name?: string;
    externalId?: string;
    creatorUser?: CommonData.UserData;
    dqlFilter?: string;
    diagrams?: CustomStatisticsDiagram[];
  }

  export interface CustomStatisticsDiagram {
    id: number;
    index: number;
    type: DiagramType;
    visualType: DiagramVisualType;
    formFieldData: { formField: Form.Field; }
  }

  export interface GetRequest {
    id: number;
    fields?: Query.FieldFunction<Fields.CustomStatistics>;
    rights?: Set<string>;
  }

  export interface GetResultRequest {
    id: number;
    dql: string;
  }

  export interface CustomStatisticsCreateRequest {
    name: string;
    externalId: string;
    dqlFilter: string;
    diagrams: CustomStatisticsDiagramCreateRequest[];
  }

  export interface CustomStatisticsUpdateRequest extends CustomStatisticsCreateRequest {
    id: number;
  }

  export interface CustomStatisticsDiagramCreateRequest {
    type: DiagramType;
    visualType: DiagramVisualType;
    formFieldData: { formFieldId: number; }
  }

  export interface CustomStatisticsResult {
    diagrams: CustomStatisticsResultDiagram[];
  }

  export interface CustomStatisticsResultDiagram {
    name: string;
    visualType: DiagramVisualType;
    stats: Record<string, number>;
  }

  export enum DiagramType {
    FORM_FIELD = 'FORM_FIELD'
  }

  export enum DiagramVisualType {
    PIE_CHART = 'PIE_CHART'
  }

  export class Mapper {
    constructor(private formMapper: Form.ResourceMapper) {
    }

    public toPublicStatistics(r: CustomStatisticsResource.CustomStatistics): CustomStatistics {
      return {
        id: r.id,
        grantedRights: r.granted_rights ? Set.of(...r.granted_rights) : Set.of(),
        name: r.name,
        externalId: r.external_id,
        creatorUser: r.creator_user ? CommonData.Mapper.toPublicUser(r.creator_user) : undefined,
        dqlFilter: r.dql_filter,
        diagrams: r.diagrams ? this.toPublicDiagrams(r.diagrams) : undefined
      }
    }

    private toPublicDiagrams(diagrams: CustomStatisticsResource.CustomStatisticsDiagram[]): CustomStatisticsDiagram[] {
      return diagrams.map(d => {
        return {
          id: d.id,
          index: d.index,
          type: <DiagramType>d.type,
          visualType: <DiagramVisualType>d.visual_type,
          formFieldData: {formField: this.formMapper.toPublicField(d.form_field_data.form_field)}
        }
      });
    }

    public fromPublicStatisticsCreate(r: CustomStatisticsCreateRequest): CustomStatisticsResource.CustomStatisticsCreateRequest {
      return {
        name: r.name,
        external_id: r.externalId,
        dql_filter: r.dqlFilter,
        diagrams: this.fromPublicDiagrams(r.diagrams)
      }
    }

    public fromPublicStatisticsUpdate(r: CustomStatisticsUpdateRequest): CustomStatisticsResource.CustomStatisticsUpdateRequest {
      return {
        id: r.id,
        name: r.name,
        external_id: r.externalId,
        dql_filter: r.dqlFilter,
        diagrams: this.fromPublicDiagrams(r.diagrams)
      }
    }

    private fromPublicDiagrams(diagrams: CustomStatisticsDiagramCreateRequest[]): CustomStatisticsResource.CustomStatisticsDiagramCreateRequest[] {
      return diagrams.map(d => {
        return {
          type: d.type,
          visual_type: d.visualType,
          form_field_data: {form_field_id: d.formFieldData.formFieldId}
        }
      });
    }

    toResourceQueryRequest(
      filterField: FilterField.CustomStatistics,
      orderField: OrderField.CustomStatistics,
      fields: CustomStatistics.Fields.CustomStatistics,
      request: CustomStatistics.QueryRequest): CustomStatisticsResource.QueryRequest {
      const filter: string | undefined = DqlQuery.toOptionalFilter(filterField, request.filter);
      const order: string | undefined = DqlQuery.toOptionalOrder(orderField, request.order);
      const field: string | undefined = DqlQuery.toOptionalFields(fields, request.fields);
      return {
        filter: filter,
        order: order,
        fields: field,
        page_number: request.paging ? request.paging.pageNumber : undefined,
        number_of_items: request.paging ? request.paging.numberOfItems : undefined,
        rights: DqlQuery.RightRequestSerializer.getInstance().serialize(request.rights),
        no_progress_bar: request.noProgressBar
      };
    }

    toResourceGetRequest(
      fields: CustomStatistics.Fields.CustomStatistics,
      request: CustomStatistics.GetRequest): CustomStatisticsResource.GetRequest {
      const field: string | undefined = DqlQuery.toOptionalFields(fields, request.fields);
      return {
        id: request.id,
        fields: field,
        rights: DqlQuery.RightRequestSerializer.getInstance().serialize(request.rights)
      };
    }


    toPublicResult(result: CustomStatisticsResource.CustomStatisticsResult) {
      return {
        diagrams: result.diagrams.map(d => ({
          name: d.name,
          stats: d.stats,
          visualType: <DiagramVisualType>d.visual_type
        }))
      };
    }

  }

  export namespace Fields {

    export class CustomStatistics {

      readonly id: Query.Field = new DqlQuery.Field('id');
      readonly grantedRights: Query.Field = new DqlQuery.Field('granted_rights');
      readonly name: Query.Field = new DqlQuery.Field('name');
      readonly externalId: Query.Field = new DqlQuery.Field('external_id');
      readonly creatorUser: Query.Field = new DqlQuery.Field('creator_user');
      readonly dqlFilter: Query.Field = new DqlQuery.Field('dql_filter');
      readonly diagrams: Query.Field = new DqlQuery.Field('diagrams');

      get each(): Set<Query.Field> {
        return Set.of(
          this.id,
          this.name,
          this.externalId,
          this.creatorUser,
          this.dqlFilter,
          this.diagrams,
        );
      }

      get forList(): Set<Query.Field> {
        return Set.of(
          this.id,
          this.name,
          this.externalId,
          this.creatorUser,
          this.dqlFilter,
        );
      }

      get multiselect(): Set<Query.Field> {
        return Set.of(this.id, this.name, this.externalId);
      }
    }

  }
}
